import React, {
   useCallback,
   useContext,
   useEffect,
   useLayoutEffect,
   useMemo,
   useRef,
   useState,
   useSyncExternalStore,
} from "react";
import { ReactSVG } from "react-svg";

import {
   deleteChatMessage,
   editChatMessage,
   ensureStaffConversation,
   getMyConversation,
   getStaffConversation,
   listStaffConversations,
   markMyConversationRead,
   markStaffConversationRead,
   postMyConversationMessage,
   postStaffConversationMessage,
} from "../../api/chatService";
import { getGroups, getManagerGroups } from "../../api/groupsService";
import { getUsers } from "../../api/usersService";
import { unwrapList, readStudentGroupId } from "../../utils/groupFields";
import { ROLES, normalizeRole } from "../../auth/access";
import { UserContext } from "../../UserContext";
import useChatSocket from "../../hooks/useChatSocket";
import PopupActions from "../Common/PopupActions";
import { openPopup } from "../Common/popupUIStore";
import { ButtonUI, ChoiceUI, InputUI, SearchBarUI, StudentItemUI, TagUI } from "../UI";
import useAutoLoadMore from "../../hooks/useAutoLoadMore";
import sendIcon from "../../assets/svg/send-chat.png";
import instruireAutoLogo from "../../assets/svg/logo.svg";
import StudentItemInline from "./StudentInfo/StudentItemInline";
import {
   getAvatarColorFromName,
   getInitials,
} from "./StudentInfo/studentInfoShared";
import { setChatToastContext } from "../../stores/chatToastContext";
import { setLastReadChatMessageId } from "../../stores/chatUnreadStore";
import {
   AWAITING_REPLY_SNOOZE_HOURS,
   clearStaffChatAwaitingReply,
   clearStaffChatComposerDraft,
   clearStaffChatThreadSelection,
   countStaffChatAwaitingReply,
   dismissStaffChatAwaitingReply,
   getStaffChatAwaitingReplyState,
   getStaffChatUnreadCount,
   getStaffChatUnreadTotal,
   getStaffChatWorkspaceState,
   incrementStaffChatUnread,
   markStaffChatConversationRead,
   setStaffChatActiveTab,
   setStaffChatSelection,
   setStaffChatView,
   snoozeStaffChatAwaitingReply,
   subscribeStaffChatWorkspace,
   syncStaffChatUnreadFromConversations,
   trackStaffChatReadForReply,
} from "../../stores/staffChatWorkspaceStore";

const MAX_LEN = 2000;
const MOBILE_BP = 768;
const PAGE_SIZE = 16;
const PAGE_SIZE_MOBILE = 4;
// câmpul de scris e o plăcuță singură: toate colțurile de 24px, ca la marginea unui grup
const ALL_CORNERS = { tl: true, tr: true, bl: true, br: true };

function safeText(value) {
   return String(value ?? "").trim();
}

function readStudentName(student) {
   const first = safeText(student?.firstName);
   const last = safeText(student?.lastName);
   const full = `${first} ${last}`.trim();
   return full || safeText(student?.email) || "Student";
}

function formatMessageTime(value) {
   if (!value) return "";
   const d = new Date(value);
   if (Number.isNaN(d.getTime())) return "";
   return d.toLocaleString("ro-RO", {
      hour: "2-digit",
      minute: "2-digit",
   });
}

function readMessageDayStart(value) {
   if (!value) return NaN;
   const d = new Date(value);
   if (Number.isNaN(d.getTime())) return NaN;
   return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

function formatMessageDay(value) {
   const dayStart = readMessageDayStart(value);
   if (Number.isNaN(dayStart)) return "";

   const d = new Date(dayStart);
   const now = new Date();
   const todayStart = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
   ).getTime();
   const diffDays = Math.round((todayStart - dayStart) / 86400000);

   if (diffDays === 0) return "Astăzi";
   if (diffDays === 1) return "Ieri";

   if (diffDays > 1 && diffDays < 7) {
      const weekday = d.toLocaleDateString("ro-RO", { weekday: "long" });
      return weekday.charAt(0).toUpperCase() + weekday.slice(1);
   }

   if (d.getFullYear() === now.getFullYear()) {
      return d.toLocaleDateString("ro-RO", { day: "numeric", month: "long" });
   }

   return d.toLocaleDateString("ro-RO", {
      day: "numeric",
      month: "long",
      year: "numeric",
   });
}

function isStaffRole(role) {
   const r = normalizeRole(role);
   return r === ROLES.ADMIN || r === ROLES.MANAGER;
}

function isOwnStudentMessage(message, userId) {
   const role = safeText(message?.senderRole).toUpperCase();
   if (role === "STUDENT" || role === "USER") return true;
   return Number(message?.senderId) === Number(userId);
}

function isStaffMessage(message) {
   const role = safeText(message?.senderRole).toUpperCase();
   return role === "MANAGER" || role === "ADMIN";
}

function isStudentMessage(message) {
   const role = safeText(message?.senderRole).toUpperCase();
   return role === "STUDENT" || role === "USER";
}

function markConversationReadAndTrack(conversationId, lastMessage) {
   if (conversationId == null) return;
   markStaffChatConversationRead(conversationId, lastMessage?.id);
   if (isStudentMessage(lastMessage)) {
      trackStaffChatReadForReply(conversationId, lastMessage);
   } else {
      clearStaffChatAwaitingReply(conversationId);
   }
}

function AwaitingReplyPill({ conversationId, messageId, onAction }) {
   const [open, setOpen] = useState(false);
   const wrapRef = useRef(null);

   useEffect(() => {
      if (!open) return undefined;
      const onDoc = (event) => {
         if (!wrapRef.current?.contains(event.target)) setOpen(false);
      };
      document.addEventListener("mousedown", onDoc);
      return () => document.removeEventListener("mousedown", onDoc);
   }, [open]);

   return (
      <span
         className="staffChatPopup__awaitingWrap"
         ref={wrapRef}
         onClick={(event) => event.stopPropagation()}
         onKeyDown={(event) => event.stopPropagation()}
      >
         <TagUI
            tone="warning"
            className={"staffChatPopup__awaitingPill" + (open ? " is-open" : "")}
            title="Citit, fără răspuns — opțiuni"
            aria-expanded={open}
            aria-haspopup="menu"
            onClick={() => setOpen((v) => !v)}
         >
            Răspunzi?
         </TagUI>
         {open ? (
            <span className="staffChatPopup__awaitingMenu" role="menu">
               <button
                  type="button"
                  role="menuitem"
                  className="staffChatPopup__awaitingMenuItem"
                  onClick={() => {
                     snoozeStaffChatAwaitingReply(
                        conversationId,
                        AWAITING_REPLY_SNOOZE_HOURS,
                     );
                     setOpen(false);
                     onAction?.();
                  }}
               >
                  Amintește-mi peste {AWAITING_REPLY_SNOOZE_HOURS}h
               </button>
               <button
                  type="button"
                  role="menuitem"
                  className="staffChatPopup__awaitingMenuItem"
                  onClick={() => {
                     dismissStaffChatAwaitingReply(conversationId, messageId);
                     setOpen(false);
                     onAction?.();
                  }}
               >
                  Ascunde până la mesaj nou
               </button>
            </span>
         ) : null}
      </span>
   );
}

function readConversationStudentId(conv) {
   const raw =
      conv?.student?.id ??
      conv?.studentId ??
      conv?.student_id ??
      conv?.user?.id ??
      conv?.userId ??
      null;
   const id = Number(raw);
   return Number.isFinite(id) && id > 0 ? id : null;
}

function mergeMessageLists(prev, incoming) {
   const map = new Map();
   for (const item of prev || []) {
      if (item?.id != null) map.set(String(item.id), item);
   }
   for (const item of incoming || []) {
      if (item?.id != null) map.set(String(item.id), item);
   }
   return Array.from(map.values()).sort(
      (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
   );
}

// mesaj editat / șters (chat:message:update sau răspunsul PATCH / DELETE): doar dacă e deja în fir
function replaceMessage(list, incoming) {
   if (incoming?.id == null) return list || [];
   let found = false;
   const next = (list || []).map((item) => {
      if (String(item?.id) !== String(incoming.id)) return item;
      found = true;
      return { ...item, ...incoming };
   });
   return found ? next : list || [];
}

// bifa (chat:receipt): mesajele părții `sender` din conversație, cu id ≤ upToId, primesc
// deliveredAt / readAt („citit” pune și „livrat” unde lipsea)
function applyReceipt(list, receipt) {
   const upTo = Number(receipt?.upToId);
   if (!Number.isFinite(upTo) || receipt?.conversationId == null) return list || [];
   const fromStudent = receipt.sender === "student";
   const read = receipt.kind === "read";
   let changed = false;
   const next = (list || []).map((item) => {
      if (Number(item?.conversationId) !== Number(receipt.conversationId)) return item;
      if (Number(item.id) > upTo || isStudentMessage(item) !== fromStudent) return item;
      const patch = {};
      if (!item.deliveredAt) patch.deliveredAt = receipt.at;
      if (read && !item.readAt) patch.readAt = receipt.at;
      if (!Object.keys(patch).length) return item;
      changed = true;
      return { ...item, ...patch };
   });
   return changed ? next : list || [];
}

// backend-ul cu bife / editare trimite câmpurile (chiar și null); cel vechi nu le are deloc
function hasMessageState(message) {
   return message != null && "deletedAt" in message;
}

// mesajele celeilalte părți pe care cel care se uită nu le-a citit încă
function lastUnreadFromOtherSide(messages, staffView) {
   for (let i = (messages?.length || 0) - 1; i >= 0; i -= 1) {
      const message = messages[i];
      const fromOtherSide = staffView
         ? isStudentMessage(message)
         : !isStudentMessage(message);
      if (fromOtherSide && message?.readAt === null) return message.id;
   }
   return null;
}

/**
 * Bifa „citit” pentru cealaltă parte: cât firul e deschis și fila vizibilă, mesajele
 * necitite ale celeilalte părți se marchează citite (elevul: ale personalului; personalul:
 * ale elevului). Bifele întoarse se pun și local, în caz că socketul lipsește.
 */
function useMarkChatRead(conversationId, messages, staffView, updateMessages) {
   const unreadId = lastUnreadFromOtherSide(messages, staffView);
   const inFlightRef = useRef(false);

   useEffect(() => {
      if (conversationId == null || unreadId == null) return undefined;

      const run = () => {
         if (document.visibilityState === "hidden" || inFlightRef.current) return;
         inFlightRef.current = true;
         const request = staffView
            ? markStaffConversationRead(conversationId)
            : markMyConversationRead();
         request
            .then((result) => {
               const receipts = Array.isArray(result?.receipts) ? result.receipts : [];
               if (receipts.length) {
                  updateMessages((list) =>
                     receipts.reduce((acc, receipt) => applyReceipt(acc, receipt), list),
                  );
               }
            })
            .catch(() => {})
            .finally(() => {
               inFlightRef.current = false;
            });
      };

      const timer = window.setTimeout(run, 400);
      const onVisible = () => {
         if (document.visibilityState === "visible") run();
      };
      document.addEventListener("visibilitychange", onVisible);
      return () => {
         window.clearTimeout(timer);
         document.removeEventListener("visibilitychange", onVisible);
      };
   }, [conversationId, unreadId, staffView, updateMessages]);
}

/** Editarea și ștergerea mesajelor proprii, aceleași în toate firele. */
function useChatMessageActions(updateMessages, setError) {
   const [editing, setEditing] = useState(null);

   const startEdit = useCallback((message) => setEditing(message), []);
   const cancelEdit = useCallback(() => setEditing(null), []);

   const saveEdit = useCallback(
      async (body) => {
         if (!editing) return false;
         setError("");
         try {
            const updated = await editChatMessage(editing.id, body);
            if (updated) updateMessages((list) => replaceMessage(list, updated));
            setEditing(null);
            return true;
         } catch (e) {
            setError(e?.message || "Nu s-a putut edita mesajul.");
            return false;
         }
      },
      [editing, updateMessages, setError],
   );

   const removeMessage = useCallback(
      async (message) => {
         setError("");
         try {
            const updated = await deleteChatMessage(message.id);
            if (updated) updateMessages((list) => replaceMessage(list, updated));
            setEditing((current) =>
               current && String(current.id) === String(message.id) ? null : current,
            );
         } catch (e) {
            setError(e?.message || "Nu s-a putut șterge mesajul.");
         }
      },
      [updateMessages, setError],
   );

   return { editing, startEdit, cancelEdit, saveEdit, removeMessage };
}

function useIsMobile(bp = MOBILE_BP) {
   const [isMobile, setIsMobile] = useState(() => {
      if (typeof window === "undefined") return false;
      return window.matchMedia(`(max-width:${bp}px)`).matches;
   });

   useEffect(() => {
      if (typeof window === "undefined") return undefined;

      const mq = window.matchMedia(`(max-width:${bp}px)`);
      const onChange = (event) => setIsMobile(event.matches);

      if (mq.addEventListener) mq.addEventListener("change", onChange);
      else mq.addListener(onChange);

      return () => {
         if (mq.removeEventListener) mq.removeEventListener("change", onChange);
         else mq.removeListener(onChange);
      };
   }, [bp]);

   return isMobile;
}

function readStaffSenderLabel(message) {
   if (isAutomatedMessage(message)) return "Notificare automată";
   const name = safeText(message?.senderName);
   if (name) return name;
   return "";
}

// Mementourile trimise de sistem (monitorizarea programării): backend-ul le
// marchează cu `isAutomated`; pentru personal vin și cu numele „Mesaj automat".
function isAutomatedMessage(message) {
   return (
      message?.isAutomated === true ||
      safeText(message?.senderName) === "Mesaj automat"
   );
}

function formatHistoryTime(value) {
   if (!value) return "";
   const d = new Date(value);
   if (Number.isNaN(d.getTime())) return "";
   return d.toLocaleString("ro-RO", {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
   });
}

// Bifele de pe mesajele mele: una = trimis, două = livrat, două colorate = citit
function MessageTicks({ message }) {
   if (!hasMessageState(message)) return null;
   const state = message.readAt ? "read" : message.deliveredAt ? "delivered" : "sent";
   const label = state === "read" ? "Citit" : state === "delivered" ? "Livrat" : "Trimis";
   return (
      <span
         className={`staffChatPopup__ticks staffChatPopup__ticks--${state}`}
         role="img"
         aria-label={label}
         title={label}
      >
         <svg viewBox="0 0 21 12" aria-hidden="true" focusable="false">
            <path d="M1.5 6.5 4.8 9.8 11.5 2.5" />
            {state !== "sent" ? <path d="M9.6 8.7 10.7 9.8 17.4 2.5" /> : null}
         </svg>
      </span>
   );
}

// Sub bula apăsată: istoricul (personalul: textul șters și versiunile vechi) și, pe
// mesajele proprii, Editează / Șterge
function MessagePanel({ message, history, canModify, onEdit, onDelete }) {
   const versions = Array.isArray(message.versions) ? message.versions : [];
   return (
      <div className="staffChatPopup__msgPanel">
         {history ? (
            <ul className="staffChatPopup__history" aria-label="Istoric">
               {versions.map((version, index) => (
                  <li key={`v${index}`} className="staffChatPopup__historyItem">
                     <span className="staffChatPopup__historyLabel">
                        {formatHistoryTime(version.writtenAt)}
                     </span>
                     <p className="staffChatPopup__historyBody">{version.body}</p>
                  </li>
               ))}
               {message.deleted ? (
                  <li className="staffChatPopup__historyItem staffChatPopup__historyItem--deleted">
                     <span className="staffChatPopup__historyLabel">
                        Șters · {formatHistoryTime(message.deletedAt)}
                     </span>
                     <p className="staffChatPopup__historyBody">{message.body}</p>
                  </li>
               ) : null}
            </ul>
         ) : null}
         {canModify ? (
            <ButtonUI.Row align="end" className="staffChatPopup__msgActions">
               <ButtonUI size="sm" variant="subtle" icon="edit" onClick={() => onEdit?.(message)}>
                  Editează
               </ButtonUI>
               <ButtonUI
                  size="sm"
                  variant="danger"
                  icon="trash"
                  confirm="Da, șterge"
                  onClick={() => onDelete?.(message)}
               >
                  Șterge
               </ButtonUI>
            </ButtonUI.Row>
         ) : null}
      </div>
   );
}

function ChatMessageList({
   messages,
   viewerRole,
   userId,
   scrollRef,
   editingId = null,
   onEdit,
   onDelete,
}) {
   const bottomRef = useRef(null);
   const listRef = useRef(null);
   const [openId, setOpenId] = useState(null);
   const staffView = isStaffRole(viewerRole);
   const lastId = messages?.length ? messages[messages.length - 1]?.id : null;

   // derulăm jos doar la mesaj nou, nu când se schimbă o bifă sau se deschide un panou
   useEffect(() => {
      const scrollEl = scrollRef?.current;
      if (!scrollEl) return;
      scrollEl.scrollTop = scrollEl.scrollHeight;
   }, [lastId, messages?.length, scrollRef]);

   // panoul se închide la clic în afara bulei lui și la Escape
   useEffect(() => {
      if (openId == null) return undefined;
      const onDoc = (event) => {
         const open = listRef.current?.querySelector(".staffChatPopup__bubbleCol.is-open");
         if (open && !open.contains(event.target)) setOpenId(null);
      };
      const onKey = (event) => {
         if (event.key === "Escape") setOpenId(null);
      };
      document.addEventListener("mousedown", onDoc);
      document.addEventListener("keydown", onKey);
      return () => {
         document.removeEventListener("mousedown", onDoc);
         document.removeEventListener("keydown", onKey);
      };
   }, [openId]);

   return (
      <div className="staffChatPopup__messages" ref={listRef}>
         <div className="staffChatPopup__messagesSpacerTop" aria-hidden="true" />
         {!messages?.length ? (
            <p className="staffChatPopup__empty">Niciun mesaj încă.</p>
         ) : (
            messages.map((message, index) => {
               const own = staffView
                  ? isStaffMessage(message)
                  : isOwnStudentMessage(message, userId);
               const time = formatMessageTime(message.createdAt);
               const dayStart = readMessageDayStart(message.createdAt);
               const prev = messages[index - 1];
               const prevDayStart = readMessageDayStart(prev?.createdAt);
               const dayLabel =
                  !Number.isNaN(dayStart) && dayStart !== prevDayStart
                     ? formatMessageDay(message.createdAt)
                     : "";
               // numele celui din personal doar când se schimbă cine scrie (ca la elev: bulele curg)
               const sameSenderAsPrev =
                  !dayLabel &&
                  prev != null &&
                  isStaffMessage(prev) &&
                  readStaffSenderLabel(prev) === readStaffSenderLabel(message);
               const senderLabel =
                  staffView && isStaffMessage(message) && !sameSenderAsPrev
                     ? readStaffSenderLabel(message)
                     : "";
               const notice = isAutomatedMessage(message);
               const deleted = Boolean(message.deleted || message.deletedAt);
               const edited = !deleted && Boolean(message.editedAt);
               const mine = staffView
                  ? Number(message.senderId) === Number(userId)
                  : isOwnStudentMessage(message, userId);
               const canModify =
                  hasMessageState(message) && mine && !notice && !deleted && Boolean(onEdit);
               const history =
                  staffView &&
                  (deleted || (Array.isArray(message.versions) && message.versions.length > 0));
               const interactive = canModify || history;
               const isOpen = interactive && String(openId) === String(message.id);
               const isEditing = editingId != null && String(editingId) === String(message.id);
               const toggle = () => {
                  if (!interactive) return;
                  setOpenId((current) =>
                     String(current) === String(message.id) ? null : message.id,
                  );
               };
               return (
                  <React.Fragment key={message.id}>
                     {dayLabel ? (
                        <div
                           className="staffChatPopup__dayDivider"
                           role="separator"
                        >
                           <span className="staffChatPopup__dayDividerLabel">
                              {dayLabel}
                           </span>
                        </div>
                     ) : null}
                     <div
                        className={
                           "staffChatPopup__bubbleCol" +
                           (own
                              ? " staffChatPopup__bubbleCol--own"
                              : " staffChatPopup__bubbleCol--other") +
                           (isOpen ? " is-open" : "")
                        }
                     >
                        {senderLabel ? (
                           <span className="staffChatPopup__senderPill">
                              {senderLabel}
                           </span>
                        ) : null}
                        <div
                           className={
                              "staffChatPopup__bubble" +
                              (own
                                 ? " staffChatPopup__bubble--own"
                                 : " staffChatPopup__bubble--other") +
                              (notice ? " staffChatPopup__bubble--notice" : "") +
                              (deleted ? " staffChatPopup__bubble--deleted" : "") +
                              (interactive ? " is-interactive" : "") +
                              (isEditing ? " is-editing" : "")
                           }
                           role={interactive ? "button" : notice ? "note" : undefined}
                           tabIndex={interactive ? 0 : undefined}
                           aria-expanded={interactive ? isOpen : undefined}
                           title={
                              interactive
                                 ? history && !canModify
                                    ? "Vezi istoricul"
                                    : "Opțiuni"
                                 : undefined
                           }
                           onClick={toggle}
                           onKeyDown={(event) => {
                              if (!interactive) return;
                              if (event.key === "Enter" || event.key === " ") {
                                 event.preventDefault();
                                 toggle();
                              }
                           }}
                        >
                           <p className="staffChatPopup__bubbleBody">
                              {deleted ? (
                                 <span className="staffChatPopup__deletedText">Mesaj șters</span>
                              ) : (
                                 message.body
                              )}
                              <span
                                 className={
                                    "staffChatPopup__bubbleTime" +
                                    (notice ? " staffChatPopup__bubbleTime--notice" : "")
                                 }
                              >
                                 {notice ? (
                                    <span className="staffChatPopup__noticeTag">
                                       Notificare
                                    </span>
                                 ) : null}
                                 {notice && time ? " · " : ""}
                                 {edited ? "editat · " : ""}
                                 {time}
                                 {own && !notice && !deleted ? (
                                    <MessageTicks message={message} />
                                 ) : null}
                              </span>
                           </p>
                        </div>
                        {isOpen ? (
                           <MessagePanel
                              message={message}
                              history={history}
                              canModify={canModify}
                              onEdit={(m) => {
                                 setOpenId(null);
                                 onEdit?.(m);
                              }}
                              onDelete={(m) => {
                                 setOpenId(null);
                                 onDelete?.(m);
                              }}
                           />
                        ) : null}
                     </div>
                  </React.Fragment>
               );
            })
         )}
         <div
            className="staffChatPopup__messagesSpacerBottom"
            aria-hidden="true"
         />
         <div ref={bottomRef} aria-hidden="true" />
      </div>
   );
}

function ChatThreadBody({
   head,
   headClassName = "",
   error,
   loading,
   messages,
   viewerRole,
   userId,
   composer,
   editingId = null,
   onEditMessage,
   onDeleteMessage,
}) {
   const threadRef = useRef(null);
   const headRef = useRef(null);
   const composerRef = useRef(null);
   const scrollRef = useRef(null);

   const syncOffsets = useCallback(() => {
      const thread = threadRef.current;
      if (!thread) return;
      thread.style.setProperty(
         "--chat-head-h",
         `${headRef.current?.offsetHeight ?? 0}px`,
      );
      thread.style.setProperty(
         "--chat-compose-h",
         `${composerRef.current?.offsetHeight ?? 0}px`,
      );
   }, []);

   useLayoutEffect(() => {
      syncOffsets();
      const observer = new ResizeObserver(syncOffsets);
      if (headRef.current) observer.observe(headRef.current);
      if (composerRef.current) observer.observe(composerRef.current);
      return () => observer.disconnect();
   }, [syncOffsets, head, composer, loading, messages?.length]);

   return (
      <div className="staffChatPopup__thread" ref={threadRef}>
         <div className="staffChatPopup__threadScroll" ref={scrollRef}>
            {error ? <p className="staffChatPopup__error">{error}</p> : null}
            {loading ? (
               <p className="staffChatPopup__state">Se încarcă...</p>
            ) : (
               <ChatMessageList
                  messages={messages}
                  viewerRole={viewerRole}
                  userId={userId}
                  scrollRef={scrollRef}
                  editingId={editingId}
                  onEdit={onEditMessage}
                  onDelete={onDeleteMessage}
               />
            )}
         </div>

         {/* blurul progresiv de sus și de jos, pe toată lățimea */}
         <div className="staffChatPopup__threadBlur" aria-hidden="true" />
         <div className="staffChatPopup__threadBlur staffChatPopup__threadBlur--bottom" aria-hidden="true" />

         <div
            ref={headRef}
            className={`staffChatPopup__threadHead ${headClassName}`.trim()}
         >
            {head}
         </div>

         {React.isValidElement(composer)
            ? React.cloneElement(composer, { formRef: composerRef })
            : composer}
      </div>
   );
}

function InstruireAutoChatProfile() {
   const [logoFailed, setLogoFailed] = useState(false);
   const appIconSrc = `${process.env.PUBLIC_URL || ""}/android-chrome-192x192.png`;

   return (
      <div className="studentItem staffChatPopup__threadProfile">
         <div className="studentItem__avatar staffChatPopup__brandAvatar">
            {!logoFailed ? (
               <img
                  className="staffChatPopup__brandLogoImg"
                  src={appIconSrc}
                  alt=""
                  aria-hidden="true"
                  onError={() => setLogoFailed(true)}
               />
            ) : (
               <ReactSVG
                  src={instruireAutoLogo}
                  className="staffChatPopup__brandLogoSvg"
                  aria-hidden="true"
               />
            )}
         </div>
         <div className="studentItem__info">
            <h3 className="studentItem__name">Instruire Auto</h3>
            <p className="studentItem__meta">Echipa Instruire Auto</p>
         </div>
      </div>
   );
}

function ChatComposer({
   disabled,
   sending,
   onSend,
   placeholder,
   formRef,
   draftText = "",
   draftToken = 0,
   // mesajul propriu în editare: câmpul ia textul lui, „Trimite” devine „Salvează”
   editing = null,
   onSaveEdit,
   onCancelEdit,
}) {
   const [text, setText] = useState("");
   const inputRef = useRef(null);
   const editingId = editing?.id ?? null;
   const draftBeforeEditRef = useRef("");

   const resizeInput = useCallback(() => {
      const el = inputRef.current;
      if (!el) return;
      el.style.height = "auto";
      const styles = window.getComputedStyle(el);
      const lineHeight = parseFloat(styles.lineHeight) || 18;
      const padY =
         parseFloat(styles.paddingTop) + parseFloat(styles.paddingBottom);
      const maxHeight = lineHeight * 5 + padY;
      el.style.height = `${Math.min(el.scrollHeight, maxHeight)}px`;
   }, []);

   const focusInput = useCallback(() => {
      requestAnimationFrame(() => {
         inputRef.current?.focus();
      });
   }, []);

   useEffect(() => {
      if (!draftToken) return;
      const next = String(draftText || "");
      if (!next) {
         clearStaffChatComposerDraft();
         return;
      }
      setText(next);
      clearStaffChatComposerDraft();
      focusInput();
   }, [draftToken, draftText, focusInput]);

   // la intrarea în editare păstrăm ce era scris; la ieșire îl punem la loc
   useEffect(() => {
      if (editingId == null) return undefined;
      setText((current) => {
         draftBeforeEditRef.current = current;
         return String(editing?.body || "");
      });
      focusInput();
      return () => {
         setText(draftBeforeEditRef.current);
         draftBeforeEditRef.current = "";
      };
      // doar când se schimbă mesajul editat, nu la fiecare bifă venită pe el
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [editingId, focusInput]);

   useEffect(() => {
      resizeInput();
   }, [text, resizeInput]);

   const handleSubmit = async (event) => {
      event.preventDefault();
      const body = safeText(text);
      if (!body || disabled || sending) return;
      if (editingId != null) {
         if (body === safeText(editing?.body)) {
            onCancelEdit?.();
            return;
         }
         await onSaveEdit?.(body);
         focusInput();
         return;
      }
      const ok = await onSend(body);
      if (ok) {
         setText("");
         focusInput();
      }
   };

   return (
      <form
         ref={formRef}
         className={
            "staffChatPopup__composer staffChatPopup__composer--absolute" +
            (editingId != null ? " is-editing" : "")
         }
         onSubmit={handleSubmit}
      >
         {editingId != null ? (
            <div className="staffChatPopup__editBar">
               <span className="staffChatPopup__editBarLabel">Editezi mesajul</span>
               <ButtonUI
                  size="sm"
                  variant="ghost"
                  icon="close"
                  aria-label="Anulează editarea"
                  title="Anulează"
                  onClick={() => onCancelEdit?.()}
               />
            </div>
         ) : null}
         <InputUI
            as="textarea"
            ref={inputRef}
            className="staffChatPopup__input"
            corners={ALL_CORNERS}
            rows={1}
            maxLength={MAX_LEN}
            value={text}
            placeholder={placeholder}
            aria-label="Mesajul"
            disabled={disabled || sending}
            onChange={(event) => setText(event.target.value)}
            onInput={resizeInput}
            onKeyDown={(event) => {
               if (event.key === "Enter" && !event.shiftKey) {
                  event.preventDefault();
                  handleSubmit(event);
               } else if (event.key === "Escape" && editingId != null) {
                  event.preventDefault();
                  onCancelEdit?.();
               }
            }}
         />
         <ButtonUI
            type="submit"
            variant="success-soft"
            className="staffChatPopup__send"
            icon={
               editingId != null ? (
                  "check"
               ) : (
                  <img className="staffChatPopup__sendIcon" src={sendIcon} alt="" />
               )
            }
            disabled={disabled || sending || !safeText(text)}
            aria-label={editingId != null ? "Salvează mesajul" : "Trimite mesajul"}
            title={editingId != null ? "Salvează" : "Trimite"}
         />
      </form>
   );
}

function StudentChatView() {
   const { user } = useContext(UserContext);
   const [conversation, setConversation] = useState(null);
   const [loading, setLoading] = useState(true);
   const [error, setError] = useState("");
   const [sending, setSending] = useState(false);

   const load = useCallback(async () => {
      setLoading(true);
      setError("");
      try {
         const data = await getMyConversation();
         setConversation(data);
      } catch (e) {
         setError(e?.message || "Nu s-a putut încărca conversația.");
      } finally {
         setLoading(false);
      }
   }, []);

   useEffect(() => {
      load();
   }, [load]);

   useEffect(() => {
      setChatToastContext({
         popupOpen: true,
         conversationId: conversation?.id ?? null,
      });
   }, [conversation?.id]);

   useEffect(() => {
      const messages = conversation?.messages;
      if (!messages?.length) return;
      const last = messages[messages.length - 1];
      if (last?.id != null) setLastReadChatMessageId(last.id);
   }, [conversation?.messages]);

   useEffect(
      () => () => {
         setChatToastContext({ popupOpen: false, conversationId: null });
      },
      [],
   );

   // schimbă doar lista de mesaje (editare, ștergere, bife)
   const updateMessages = useCallback((fn) => {
      setConversation((prev) => (prev ? { ...prev, messages: fn(prev.messages || []) } : prev));
   }, []);

   const { editing, startEdit, cancelEdit, saveEdit, removeMessage } =
      useChatMessageActions(updateMessages, setError);

   useMarkChatRead(conversation?.id ?? null, conversation?.messages, false, updateMessages);

   useChatSocket(
      true,
      (message) => {
         if (!message || message.conversationId == null) return;
         setConversation((prev) => {
            if (!prev) return prev;
            if (Number(prev.id) !== Number(message.conversationId)) return prev;
            return {
               ...prev,
               messages: mergeMessageLists(prev.messages, [message]),
            };
         });
      },
      {
         onUpdate: (message) => updateMessages((list) => replaceMessage(list, message)),
         onReceipt: (receipt) => updateMessages((list) => applyReceipt(list, receipt)),
      },
   );

   const handleSend = useCallback(
      async (body) => {
         setSending(true);
         setError("");
         try {
            const message = await postMyConversationMessage(body);
            setConversation((prev) => ({
               ...(prev || {}),
               id: prev?.id ?? message?.conversationId,
               messages: mergeMessageLists(prev?.messages, [message]),
            }));
            return true;
         } catch (e) {
            setError(e?.message || "Nu s-a putut trimite mesajul.");
            return false;
         } finally {
            setSending(false);
         }
      },
      [],
   );

   return (
      <ChatThreadBody
         head={<InstruireAutoChatProfile />}
         error={error}
         loading={loading}
         messages={conversation?.messages || []}
         viewerRole={user?.role}
         userId={user?.id}
         editingId={editing?.id ?? null}
         onEditMessage={startEdit}
         onDeleteMessage={removeMessage}
         composer={
            <ChatComposer
               disabled={loading}
               sending={sending}
               onSend={handleSend}
               placeholder="Scrie un mesaj managerului..."
               editing={editing}
               onSaveEdit={saveEdit}
               onCancelEdit={cancelEdit}
            />
         }
      />
   );
}

function StaffChatInboxView({
   onConversationChange,
   initialConversationId = null,
   initialStudent = null,
}) {
   const { user } = useContext(UserContext);
   const isMobile = useIsMobile();
   const pageSize = isMobile ? PAGE_SIZE_MOBILE : PAGE_SIZE;
   const isManagerRole = normalizeRole(user?.role) === ROLES.MANAGER;
   const restrictToMyGroups = isManagerRole;

   const workspace = useSyncExternalStore(
      subscribeStaffChatWorkspace,
      getStaffChatWorkspaceState,
      getStaffChatWorkspaceState,
   );

   const [students, setStudents] = useState([]);
   const [conversations, setConversations] = useState([]);
   const [groups, setGroups] = useState([]);
   // null = admin (fără restricție). Set = grupele atribuite managerului.
   const [managerGroupIds, setManagerGroupIds] = useState(null);
   const [thread, setThread] = useState(null);
   const [studentMetaById, setStudentMetaById] = useState({});
   const [loadingList, setLoadingList] = useState(true);
   const [loadingThread, setLoadingThread] = useState(false);
   const [error, setError] = useState("");
   const [sending, setSending] = useState(false);
   const [searchQuery, setSearchQuery] = useState("");
   const [visibleCount, setVisibleCount] = useState(pageSize);
   const [nowTick, setNowTick] = useState(() => Date.now());
   const selectedIdRef = useRef(null);
   const threadReqRef = useRef(0);

   const view = workspace.view === "thread" ? "thread" : "list";
   // „Grupele mele” există doar la manager (adminul n-are grupe atribuite)
   const tabKeys = restrictToMyGroups
      ? ["conversations", "unread", "groups", "contacts"]
      : ["conversations", "unread", "contacts"];
   const activeTab = tabKeys.includes(workspace.activeTab) ? workspace.activeTab : "conversations";
   const selectedId = workspace.selectedConversationId;
   const selectedStudent = workspace.selectedStudent;
   const selectedStudentId =
      selectedStudent?.id != null ? Number(selectedStudent.id) : null;

   useEffect(() => {
      onConversationChange?.(view === "thread");
   }, [view, onConversationChange]);

   useEffect(() => {
      setChatToastContext({
         popupOpen: workspace.isOpen && workspace.dockMode === "expanded",
         conversationId: view === "thread" ? selectedId : null,
      });
   }, [view, selectedId, workspace.isOpen, workspace.dockMode]);

   useEffect(() => {
      if (initialConversationId == null && !initialStudent?.id) return;
      setStaffChatSelection({
         view: "thread",
         conversationId:
            initialConversationId != null ? initialConversationId : null,
         student: initialStudent || null,
      });
   }, [initialConversationId, initialStudent]);

   useEffect(() => {
      selectedIdRef.current = selectedId;
   }, [selectedId]);

   useEffect(() => {
      setVisibleCount(pageSize);
   }, [searchQuery, students.length, pageSize, activeTab]);

   const loadStudentsAndMeta = useCallback(async () => {
      try {
         // Users and groups are fetched independently: a groups failure must
         // not wipe the whole contacts list (that produced "both tabs empty").
         const users = unwrapList(await getUsers());

         let groups = [];
         try {
            groups = await getGroups();
         } catch {
            groups = [];
         }

         const groupNameById = {};
         for (const group of groups || []) {
            if (group?.id != null) {
               groupNameById[String(group.id)] =
                  safeText(group.name) || `Grupa ${group.id}`;
            }
         }

         const studentList = [];
         const map = {};
         for (const student of users || []) {
            if (!student?.id) continue;
            const role = normalizeRole(student.role);
            if (role !== ROLES.USER) continue;
            // cui nu i se poate scrie: oaspeții, conturile șterse și elevii fără grupă (chatul
            // există doar pentru elevii unei grupe — backend-ul refuză)
            if (student.isGuest === true || student.deletedAt || readStudentGroupId(student) == null) continue;
            studentList.push(student);
            const groupId = readStudentGroupId(student);
            map[String(student.id)] = {
               phone: safeText(student.phone || student.phoneNumber),
               groupName: groupId
                  ? groupNameById[String(groupId)] || `Grupa ${groupId}`
                  : "Fără grupă",
            };
         }

         studentList.sort((a, b) =>
            readStudentName(a).localeCompare(readStudentName(b), "ro"),
         );
         setStudents(studentList);
         setStudentMetaById(map);
      } catch {
         // optional enrichment
      }
   }, []);

   const loadGroups = useCallback(async () => {
      if (!user?.id) {
         setGroups([]);
         setManagerGroupIds(restrictToMyGroups ? new Set() : null);
         return;
      }
      try {
         if (restrictToMyGroups) {
            const arr = await getManagerGroups(user.id);
            setGroups(arr);
            setManagerGroupIds(
               new Set(
                  arr
                     .map((g) => String(g?.id ?? g?.groupId ?? ""))
                     .filter(Boolean),
               ),
            );
         } else {
            const arr = await getGroups();
            setGroups(arr);
            setManagerGroupIds(null);
         }
      } catch {
         setGroups([]);
         setManagerGroupIds(restrictToMyGroups ? new Set() : null);
      }
   }, [user?.id, restrictToMyGroups]);

   const loadList = useCallback(async () => {
      setLoadingList(true);
      setError("");
      try {
         const data = await listStaffConversations();
         const sorted = Array.isArray(data)
            ? data.slice().sort(
                 (a, b) =>
                    new Date(
                       b.updatedAt || b.lastMessage?.createdAt || 0,
                    ).getTime() -
                    new Date(
                       a.updatedAt || a.lastMessage?.createdAt || 0,
                    ).getTime(),
              )
            : [];
         setConversations(sorted);
         syncStaffChatUnreadFromConversations(sorted, selectedIdRef.current);
      } catch (e) {
         setError(e?.message || "Nu s-au putut încărca conversațiile.");
      } finally {
         setLoadingList(false);
      }
   }, []);

   const loadThread = useCallback(async (id) => {
      if (id == null) return;
      const requestId = ++threadReqRef.current;
      const conversationId = Number(id);
      setLoadingThread(true);
      setError("");
      try {
         const data = await getStaffConversation(conversationId);
         if (requestId !== threadReqRef.current) return;
         setThread(data);
         if (data?.student?.id != null) {
            const current = getStaffChatWorkspaceState();
            if (
               Number(current.selectedStudent?.id) !== Number(data.student.id)
            ) {
               setStaffChatSelection({ student: data.student });
            }
         }
         const msgs = Array.isArray(data?.messages) ? data.messages : [];
         const lastMsg = msgs[msgs.length - 1] || data?.lastMessage;
         markConversationReadAndTrack(conversationId, lastMsg);
      } catch (e) {
         if (requestId !== threadReqRef.current) return;
         setError(e?.message || "Nu s-a putut încărca conversația.");
         setThread(null);
      } finally {
         if (requestId === threadReqRef.current) {
            setLoadingThread(false);
         }
      }
   }, []);

   useEffect(() => {
      loadStudentsAndMeta();
      loadGroups();
      loadList();
   }, [loadStudentsAndMeta, loadGroups, loadList]);

   useEffect(() => {
      if (!groups.length || !students.length) return;
      const nameById = {};
      for (const group of groups) {
         const id = group?.id ?? group?.groupId;
         if (id == null) continue;
         nameById[String(id)] = safeText(group.name) || `Grupa ${id}`;
      }
      setStudentMetaById((prev) => {
         let changed = false;
         const next = { ...prev };
         for (const student of students) {
            if (student?.id == null) continue;
            const gid = student.groupId ?? student.group?.id;
            if (gid == null || !nameById[String(gid)]) continue;
            const key = String(student.id);
            const current = next[key] || {};
            if (current.groupName === nameById[String(gid)]) continue;
            next[key] = { ...current, groupName: nameById[String(gid)] };
            changed = true;
         }
         return changed ? next : prev;
      });
   }, [groups, students]);

   // Re-evaluate „Răspunzi?” grace period while the list is open.
   useEffect(() => {
      if (view !== "list") return undefined;
      const id = window.setInterval(() => setNowTick(Date.now()), 60_000);
      return () => window.clearInterval(id);
   }, [view]);

   useEffect(() => {
      if (view !== "thread") return undefined;

      if (selectedId != null) {
         loadThread(selectedId);
         return undefined;
      }

      if (selectedStudentId != null) {
         // Deschiderea pe student (ex. „Răspunde" la o recenzie) nu aduce un
         // conversationId. Dacă exista deja o conversație cu elevul, o
         // selectăm: altfel firul s-ar deschide gol, istoricul ar părea
         // pierdut si am cere inutil un POST /chat/conversations.
         const existingId = (conversations || []).reduce((found, conv) => {
            if (found != null) return found;
            const studentId = readConversationStudentId(conv);
            if (studentId == null) return found;
            if (studentId !== Number(selectedStudentId)) return found;
            const convId = Number(conv?.id);
            return Number.isFinite(convId) && convId > 0 ? convId : found;
         }, null);

         if (existingId != null) {
            setStaffChatSelection({ conversationId: existingId });
            return undefined;
         }

         setThread({
            id: null,
            student: selectedStudent,
            messages: [],
         });
         setLoadingThread(false);
      }
      return undefined;
      // selectedStudent object identity must not retrigger loads
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [view, selectedId, selectedStudentId, loadThread, conversations]);

   // schimbă doar mesajele firului deschis (editare, ștergere, bife)
   const updateMessages = useCallback((fn) => {
      setThread((prev) => (prev ? { ...prev, messages: fn(prev.messages || []) } : prev));
   }, []);

   const { editing, startEdit, cancelEdit, saveEdit, removeMessage } =
      useChatMessageActions(updateMessages, setError);

   // altă conversație sau înapoi la listă: editarea începută nu trece mai departe
   useEffect(() => {
      cancelEdit();
   }, [selectedId, view, cancelEdit]);

   useMarkChatRead(
      view === "thread" && thread?.id != null ? thread.id : null,
      thread?.messages,
      true,
      updateMessages,
   );

   // previzualizarea din listă urmează și ea editarea / ștergerea / bifele ultimului mesaj
   const updateLastMessages = useCallback((fn) => {
      setConversations((prev) => {
         let changed = false;
         const next = (prev || []).map((conv) => {
            if (!conv?.lastMessage) return conv;
            const [updated] = fn([conv.lastMessage]);
            if (updated === conv.lastMessage) return conv;
            changed = true;
            return { ...conv, lastMessage: updated };
         });
         return changed ? next : prev;
      });
   }, []);

   useChatSocket(true, (message) => {
      if (!message?.conversationId) return;
      setConversations((prev) => {
         const list = Array.isArray(prev) ? prev.slice() : [];
         const idx = list.findIndex(
            (item) => Number(item.id) === Number(message.conversationId),
         );
         if (idx >= 0) {
            const current = list[idx];
            list[idx] = {
               ...current,
               updatedAt: message.createdAt || new Date().toISOString(),
               lastMessage: message,
            };
            list.sort(
               (a, b) =>
                  new Date(b.updatedAt).getTime() -
                  new Date(a.updatedAt).getTime(),
            );
            return list;
         }
         loadList();
         return prev;
      });

      if (Number(message.conversationId) === Number(selectedIdRef.current)) {
         // Viewing this thread — treat as read (survives refresh).
         markConversationReadAndTrack(message.conversationId, message);
      } else if (isStudentMessage(message)) {
         clearStaffChatAwaitingReply(message.conversationId);
         incrementStaffChatUnread(message.conversationId, 1);
      } else if (isStaffMessage(message)) {
         clearStaffChatAwaitingReply(message.conversationId);
      }

      setThread((prev) => {
         if (!prev || Number(prev.id) !== Number(message.conversationId)) {
            return prev;
         }
         return {
            ...prev,
            messages: mergeMessageLists(prev.messages, [message]),
         };
      });
   }, {
      onUpdate: (message) => {
         updateMessages((list) => replaceMessage(list, message));
         updateLastMessages((list) => replaceMessage(list, message));
      },
      onReceipt: (receipt) => {
         updateMessages((list) => applyReceipt(list, receipt));
         updateLastMessages((list) => applyReceipt(list, receipt));
      },
   });

   const studentGroupById = useMemo(() => {
      const map = new Map();
      for (const student of students || []) {
         if (student?.id == null) continue;
         const gid = readStudentGroupId(student);
         map.set(String(student.id), gid != null ? String(gid) : null);
      }
      return map;
   }, [students]);

   const getRowGroupId = useCallback(
      (student) => {
         const direct = readStudentGroupId(student);
         if (direct != null) return String(direct);
         const fromMap = studentGroupById.get(String(student?.id));
         return fromMap != null ? String(fromMap) : null;
      },
      [studentGroupById],
   );

   const inMyGroups = useCallback(
      (student) => {
         if (!restrictToMyGroups) return false;
         if (!managerGroupIds || managerGroupIds.size === 0) return false;
         const gid = getRowGroupId(student);
         return gid != null && managerGroupIds.has(String(gid));
      },
      [getRowGroupId, managerGroupIds, restrictToMyGroups],
   );

   const isInManagerScope = useCallback(
      (student) => {
         if (!restrictToMyGroups) return true;
         return inMyGroups(student);
      },
      [restrictToMyGroups, inMyGroups],
   );

   const studentsById = useMemo(() => {
      const map = new Map();
      for (const student of students || []) {
         if (student?.id != null) map.set(String(student.id), student);
      }
      return map;
   }, [students]);

   const resolveConversationStudent = useCallback(
      (conv) => {
         const nested = conv?.student;
         if (nested?.id != null) {
            const fromList = studentsById.get(String(nested.id));
            return fromList ? { ...fromList, ...nested } : nested;
         }
         const sid = conv?.studentId ?? conv?.student_id;
         if (sid != null) {
            const fromList = studentsById.get(String(sid));
            return fromList ?? { id: sid };
         }
         return null;
      },
      [studentsById],
   );

   const conversationHasMessages = useCallback((conv) => {
      if (conv?.lastMessage?.body || conv?.lastMessage?.id) return true;
      if (Number(conv?.messageCount) > 0) return true;
      return Array.isArray(conv?.messages) && conv.messages.length > 0;
   }, []);

   const conversationRows = useMemo(() => {
      const now = nowTick;
      const prioritizeMyGroups =
         restrictToMyGroups && managerGroupIds && managerGroupIds.size > 0;
      return (conversations || [])
         .map((conv) => {
            if (!conversationHasMessages(conv)) return null;
            const student = resolveConversationStudent(conv);
            if (!student?.id) return null;
            const lastBody = conv?.lastMessage?.deleted
               ? "Mesaj șters"
               : safeText(conv?.lastMessage?.body);
            const updatedAt =
               conv?.updatedAt || conv?.lastMessage?.createdAt || null;
            const conversationId = conv?.id ?? null;
            const unreadCount =
               conversationId != null
                  ? getStaffChatUnreadCount(conversationId)
                  : 0;
            const awaiting = getStaffChatAwaitingReplyState(conversationId, {
               lastMessage: conv.lastMessage,
               unreadCount,
               now,
            });
            return {
               student,
               conversationId,
               lastMessage: conv?.lastMessage ?? null,
               updatedAt,
               preview: lastBody || "Niciun mesaj încă",
               avatarColor:
                  getAvatarColorFromName(student) || "hsl(210 90% 84%)",
               initials: getInitials(student),
               unreadCount,
               awaitingReply: awaiting.show,
               awaitingAgeMs: awaiting.ageMs,
               inMyGroup: inMyGroups(student),
            };
         })
         .filter(Boolean)
         .sort((a, b) => {
            if (prioritizeMyGroups) {
               const inA = a.inMyGroup ? 1 : 0;
               const inB = b.inMyGroup ? 1 : 0;
               if (inB !== inA) return inB - inA;
            }

            const unreadA = a.unreadCount > 0 ? 1 : 0;
            const unreadB = b.unreadCount > 0 ? 1 : 0;
            if (unreadB !== unreadA) return unreadB - unreadA;

            const awaitA = a.awaitingReply ? 1 : 0;
            const awaitB = b.awaitingReply ? 1 : 0;
            if (awaitB !== awaitA) return awaitB - awaitA;
            if (awaitA && awaitB) {
               return (b.awaitingAgeMs || 0) - (a.awaitingAgeMs || 0);
            }

            const ta = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
            const tb = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
            if (tb !== ta) return tb - ta;
            return readStudentName(a.student).localeCompare(
               readStudentName(b.student),
               "ro",
            );
         });
   }, [
      conversations,
      nowTick,
      workspace.awaitingReplyVersion,
      workspace.unreadByConversationId,
      managerGroupIds,
      inMyGroups,
      restrictToMyGroups,
      conversationHasMessages,
      resolveConversationStudent,
   ]);

   const contactRows = useMemo(() => {
      const convByStudentId = new Map();
      for (const conv of conversations || []) {
         const student = resolveConversationStudent(conv);
         const studentId = student?.id ?? conv?.studentId;
         if (studentId != null) convByStudentId.set(String(studentId), conv);
      }

      return (students || [])
         .map((student) => {
            const conv = convByStudentId.get(String(student.id));
            const mergedStudent = conv?.student
               ? { ...student, ...conv.student }
               : student;
            const lastBody = conv?.lastMessage?.deleted
               ? "Mesaj șters"
               : safeText(conv?.lastMessage?.body);
            const updatedAt =
               conv?.updatedAt || conv?.lastMessage?.createdAt || null;
            return {
               student: mergedStudent,
               conversationId: conv?.id ?? null,
               lastMessage: conv?.lastMessage ?? null,
               updatedAt,
               preview: lastBody || "Niciun mesaj încă",
               avatarColor:
                  getAvatarColorFromName(mergedStudent) || "hsl(210 90% 84%)",
               initials: getInitials(mergedStudent),
            };
         })
         .sort((a, b) =>
            readStudentName(a.student).localeCompare(
               readStudentName(b.student),
               "ro",
            ),
         );
   }, [students, conversations, resolveConversationStudent]);

   const query = useMemo(
      () => searchQuery.trim().toLowerCase(),
      [searchQuery],
   );

   const sourceRows = useMemo(() => {
      if (activeTab === "unread") {
         return conversationRows.filter(
            (row) => isInManagerScope(row.student) && (row.unreadCount > 0 || row.awaitingReply),
         );
      }
      if (activeTab === "groups") {
         return contactRows.filter((row) => inMyGroups(row.student));
      }
      if (activeTab === "contacts") {
         return contactRows.filter((row) => isInManagerScope(row.student));
      }
      return conversationRows.filter((row) => isInManagerScope(row.student));
   }, [
      activeTab,
      contactRows,
      conversationRows,
      inMyGroups,
      isInManagerScope,
   ]);

   const filteredRows = useMemo(() => {
      if (!query) return sourceRows;
      return sourceRows.filter((row) => {
         const meta = studentMetaById[String(row.student?.id)] || {};
         const haystack = [
            readStudentName(row.student),
            row.student?.email,
            meta.phone,
            meta.groupName,
            row.preview,
         ]
            .map((part) => safeText(part).toLowerCase())
            .join(" ");
         return haystack.includes(query);
      });
   }, [sourceRows, query, studentMetaById]);

   const visibleRows = useMemo(
      () => filteredRows.slice(0, visibleCount),
      [filteredRows, visibleCount],
   );

   const totalResults = filteredRows.length;
   const canLoadMore = visibleCount < totalResults;

   // Cand firul e deschis pe un student „subtire" (doar id, venit dintr-o
   // recenzie fara `user` embedat), completam numele din lista incarcata.
   const activeStudentBase = selectedStudent || thread?.student;
   const activeStudent = (() => {
      if (activeStudentBase?.id == null) return activeStudentBase;
      const fromList = studentsById.get(String(activeStudentBase.id));
      if (!fromList) return activeStudentBase;
      return { ...fromList, ...activeStudentBase };
   })();
   const activeMeta = studentMetaById[String(activeStudent?.id)] || {};
   const activeColor =
      getAvatarColorFromName(activeStudent) || "hsl(210 90% 84%)";

   const handleSelectRow = useCallback(
      (row) => {
         const conversationId =
            row.conversationId != null ? Number(row.conversationId) : null;
         setStaffChatSelection({
            view: "thread",
            student: row.student,
            conversationId:
               Number.isFinite(conversationId) && conversationId > 0
                  ? conversationId
                  : null,
         });
         if (Number.isFinite(conversationId) && conversationId > 0) {
            // Load immediately on click (don't wait only on effect).
            loadThread(conversationId);
            markConversationReadAndTrack(conversationId, row.lastMessage);
         } else {
            threadReqRef.current += 1;
            setLoadingThread(false);
            setThread({
               id: null,
               student: row.student,
               messages: [],
            });
         }
      },
      [loadThread],
   );

   const handleBackToList = useCallback(() => {
      clearStaffChatThreadSelection();
      setThread(null);
      setError("");
   }, []);

   // următoarea pagină a listei, la derulare spre capăt (useAutoLoadMore cere o țintă fixă)
   const autoLoad = useAutoLoadMore(view === "list" && !loadingList && canLoadMore, () =>
      setVisibleCount((count) => Math.max(count, Math.min(visibleCount + pageSize, totalResults))),
   );

   const handleSend = useCallback(
      async (body) => {
         setSending(true);
         setError("");
         try {
            let conversationId = selectedId;
            if (conversationId == null && selectedStudent?.id != null) {
               const created = await ensureStaffConversation(selectedStudent.id);
               conversationId = created?.id ?? null;
               if (conversationId != null) {
                  setStaffChatSelection({ conversationId });
                  await loadList();
               }
            }
            if (conversationId == null) {
               throw new Error("Nu s-a putut deschide conversația.");
            }

            const message = await postStaffConversationMessage(
               conversationId,
               body,
            );
            clearStaffChatAwaitingReply(conversationId);
            setThread((prev) =>
               prev
                  ? {
                       ...prev,
                       id: conversationId,
                       messages: mergeMessageLists(prev.messages, [message]),
                    }
                  : {
                       id: conversationId,
                       student: selectedStudent,
                       messages: [message],
                    },
            );
            await loadList();
            return true;
         } catch (e) {
            setError(e?.message || "Nu s-a putut trimite mesajul.");
            return false;
         } finally {
            setSending(false);
         }
      },
      [selectedId, selectedStudent, loadList],
   );

   if (view === "list") {
      const scopedConversations = restrictToMyGroups
         ? (conversations || []).filter((conv) => {
              const student = resolveConversationStudent(conv);
              return student && isInManagerScope(student);
           })
         : conversations;
      const awaitingCount = countStaffChatAwaitingReply(
         scopedConversations,
         nowTick,
      );

      const unreadTabCount = conversationRows.filter(
         (row) => isInManagerScope(row.student) && row.unreadCount > 0,
      ).length;
      // filele obișnuite din popup-uri (ChoiceUI tabs, doar text); „Necitite” cu numărul lor
      const tabOptions = [
         { value: "conversations", label: "Conversații" },
         {
            value: "unread",
            label: "Necitite",
            title: awaitingCount > 0 ? `Necitite · ${awaitingCount} fără răspuns` : "Necitite",
            count: unreadTabCount || undefined,
            dot: awaitingCount > 0 && !unreadTabCount ? "Fără răspuns" : undefined,
         },
         restrictToMyGroups ? { value: "groups", label: "Grupele mele" } : null,
         { value: "contacts", label: "Elevi" },
      ].filter(Boolean);
      const placeholder =
         activeTab === "contacts" || activeTab === "groups"
            ? restrictToMyGroups
               ? "Caută elev din grupe…"
               : "Caută elev…"
            : "Caută conversație…";
      const emptyText =
         activeTab === "unread"
            ? "Nimic necitit."
            : activeTab === "conversations"
              ? "Nicio conversație cu mesaje încă."
              : activeTab === "groups" || restrictToMyGroups
                ? "Niciun elev în grupele tale."
                : "Nu s-au găsit elevi.";
      const isConversationTab = activeTab === "conversations" || activeTab === "unread";

      return (
         <div className="staffChatPopup__listView">
            <ChoiceUI
               look="tabs"
               className="staffChatPopup__tabs"
               aria-label="Mesaje"
               value={activeTab}
               onChange={(v) => setStaffChatActiveTab(v)}
               options={tabOptions}
            />

            <SearchBarUI
               className="staffChatPopup__search"
               placeholder={placeholder}
               value={searchQuery}
               onChange={(event) => setSearchQuery(event.target.value)}
               onClear={() => setSearchQuery("")}
            />

            <div className="staffChatPopup__gridWrap">
               {/* fără „Afișează încă”: următorii vin singuri aproape de capătul listei, ca pe Acasă */}
               <div
                  className="staffChatPopup__grid staffChatPopup__grid--rows"
                  ref={autoLoad.ref}
                  onScroll={autoLoad.onScroll}
               >
                  {loadingList &&
                  ((isConversationTab && !conversations.length) ||
                     (!isConversationTab && !students.length && !conversations.length)) ? (
                     <p className="staffChatPopup__state">Se încarcă...</p>
                  ) : null}

                  {!loadingList &&
                     visibleRows.map((row) => {
                        const unreadCount =
                           row.unreadCount != null
                              ? row.unreadCount
                              : row.conversationId != null
                                ? getStaffChatUnreadCount(row.conversationId)
                                : 0;
                        const showAwaiting = isConversationTab && row.awaitingReply && unreadCount <= 0;
                        const meta = studentMetaById[String(row.student?.id)] || {};
                        const groupLabel = meta.groupName || "Fără grupă";
                        const phone = meta.phone || safeText(row.student?.phone);
                        const name = readStudentName(row.student);
                        const lastAt = row.lastMessage?.createdAt;
                        // ora ultimului mesaj: azi doar ora, altfel ziua („Ieri”, „Marți”, „12 octombrie”)
                        const lastTime = lastAt
                           ? readMessageDayStart(lastAt) === readMessageDayStart(new Date())
                              ? formatMessageTime(lastAt)
                              : formatMessageDay(lastAt)
                           : "";
                        // rândul unui om (StudentItemUI): conversațiile — ultimul mesaj sub nume, la capăt
                        // ora și necititele / „Răspunzi?”; elevii — grupa · telefonul, cu săgeata
                        const secondaryText = isConversationTab
                           ? row.preview
                           : [groupLabel, phone].filter(Boolean).join(" · ");
                        return (
                           <StudentItemUI
                              key={row.student.id}
                              student={row.student}
                              color={row.avatarColor}
                              initials={row.initials}
                              onOpen={() => handleSelectRow(row)}
                              secondaryText={secondaryText}
                              highlightQuery={searchQuery}
                              showChevron={!isConversationTab}
                              className={"staffChatPopup__item" + (unreadCount > 0 ? " is-unread" : "")}
                              endAdornment={
                                 isConversationTab ? (
                                    <span className="staffChatPopup__rowEnd">
                                       {lastTime ? <span className="staffChatPopup__rowTime">{lastTime}</span> : null}
                                       {showAwaiting || unreadCount > 0 ? (
                                          <span className="staffChatPopup__rowTags">
                                             {showAwaiting ? (
                                                <AwaitingReplyPill
                                                   conversationId={row.conversationId}
                                                   messageId={row.lastMessage?.id}
                                                />
                                             ) : null}
                                             {unreadCount > 0 ? (
                                                <TagUI
                                                   tone="accent"
                                                   className="staffChatPopup__unread"
                                                   aria-label={`${unreadCount} mesaje necitite`}
                                                >
                                                   {unreadCount > 99 ? "99+" : unreadCount}
                                                </TagUI>
                                             ) : null}
                                          </span>
                                       ) : null}
                                    </span>
                                 ) : null
                              }
                           />
                        );
                     })}

                  {!loadingList && totalResults === 0 ? (
                     <p className="staffChatPopup__empty">{emptyText}</p>
                  ) : null}
               </div>
            </div>
         </div>
      );
   }

   return (
      <ChatThreadBody
         head={
            <>
               {/* „Înapoi la listă” stă în rândul de sus al popup-ului, lângă „Închide” (PopupActions);
                   unde mesajele nu sunt într-o ramă cu rând de sus, rămâne aici */}
               <PopupActions>
                  <ButtonUI icon="arrow" aria-label="Înapoi la conversații" title="Înapoi la conversații" onClick={handleBackToList} />
               </PopupActions>
               {/* profilul elevului se deschide apăsând pe el (fără buton separat în rândul de sus) */}
               {activeStudent ? (
                  <StudentItemInline
                     student={activeStudent}
                     color={activeColor}
                     className="staffChatPopup__threadProfile"
                     metaLeft={activeMeta.groupName || "Fără grupă"}
                     secondaryText={activeMeta.phone || ""}
                     showIdnp={false}
                     showPhoneCopy
                     onOpen={
                        activeStudent?.id
                           ? () => openPopup("studentDetails", { student: activeStudent })
                           : undefined
                     }
                     openLabel="Profilul elevului"
                  />
               ) : (
                  <InstruireAutoChatProfile />
               )}
            </>
         }
         headClassName="staffChatPopup__threadHead--staff"
         error={error}
         loading={loadingThread && selectedId != null}
         messages={thread?.messages || []}
         viewerRole={user?.role}
         userId={user?.id}
         editingId={editing?.id ?? null}
         onEditMessage={startEdit}
         onDeleteMessage={removeMessage}
         composer={
            <ChatComposer
               disabled={loadingThread && selectedId != null}
               sending={sending}
               onSend={handleSend}
               placeholder="Scrie un mesaj..."
               draftText={workspace.composerDraft}
               draftToken={workspace.composerDraftToken}
               editing={editing}
               onSaveEdit={saveEdit}
               onCancelEdit={cancelEdit}
            />
         }
      />
   );
}

/**
 * Firul de mesaje cu un singur elev, încorporat în profilul lui (tabul
 * „Mesaje"). Caută conversația existentă; dacă nu există, o creează abia la
 * primul mesaj trimis, ca simpla deschidere a tabului să nu lase conversații
 * goale în inbox.
 */
export function StudentProfileChat({ student }) {
   const { user } = useContext(UserContext);
   const studentId = Number(student?.id);
   const [conversationId, setConversationId] = useState(null);
   const [messages, setMessages] = useState([]);
   const [loading, setLoading] = useState(true);
   const [sending, setSending] = useState(false);
   const [error, setError] = useState("");
   const conversationIdRef = useRef(null);

   useEffect(() => {
      conversationIdRef.current = conversationId;
   }, [conversationId]);

   useEffect(() => {
      if (!Number.isFinite(studentId) || studentId <= 0) {
         setLoading(false);
         return undefined;
      }
      let cancelled = false;
      setLoading(true);
      setError("");
      setConversationId(null);
      setMessages([]);

      (async () => {
         try {
            const list = await listStaffConversations();
            const existing = (list || []).find(
               (conv) => readConversationStudentId(conv) === studentId,
            );
            if (cancelled || !existing) return;
            const data = await getStaffConversation(existing.id);
            if (cancelled) return;
            const msgs = Array.isArray(data?.messages) ? data.messages : [];
            setConversationId(Number(existing.id));
            setMessages(msgs);
            markConversationReadAndTrack(
               existing.id,
               msgs[msgs.length - 1] || data?.lastMessage,
            );
         } catch (e) {
            if (!cancelled) {
               setError(e?.message || "Nu s-a putut încărca conversația.");
            }
         } finally {
            if (!cancelled) setLoading(false);
         }
      })();

      return () => {
         cancelled = true;
      };
   }, [studentId]);

   const updateMessages = useCallback((fn) => setMessages((prev) => fn(prev || [])), []);

   const { editing, startEdit, cancelEdit, saveEdit, removeMessage } =
      useChatMessageActions(updateMessages, setError);

   useMarkChatRead(conversationId, messages, true, updateMessages);

   useChatSocket(
      true,
      (message) => {
         if (!message?.conversationId) return;
         if (Number(message.conversationId) !== Number(conversationIdRef.current)) {
            return;
         }
         setMessages((prev) => mergeMessageLists(prev, [message]));
         markConversationReadAndTrack(message.conversationId, message);
      },
      {
         onUpdate: (message) => updateMessages((list) => replaceMessage(list, message)),
         onReceipt: (receipt) => updateMessages((list) => applyReceipt(list, receipt)),
      },
   );

   const handleSend = useCallback(
      async (body) => {
         setSending(true);
         setError("");
         try {
            let id = conversationIdRef.current;
            if (id == null) {
               const created = await ensureStaffConversation(studentId);
               id = created?.id ?? null;
               if (id == null) {
                  throw new Error("Nu s-a putut deschide conversația.");
               }
               setConversationId(Number(id));
               conversationIdRef.current = Number(id);
               if (Array.isArray(created?.messages) && created.messages.length) {
                  setMessages((prev) => mergeMessageLists(prev, created.messages));
               }
            }
            const message = await postStaffConversationMessage(id, body);
            clearStaffChatAwaitingReply(id);
            setMessages((prev) => mergeMessageLists(prev, [message]));
            return true;
         } catch (e) {
            setError(e?.message || "Nu s-a putut trimite mesajul.");
            return false;
         } finally {
            setSending(false);
         }
      },
      [studentId],
   );

   return (
      <div className="staffChatPopup staffChatPopup--embedded">
         <ChatThreadBody
            head={null}
            error={error}
            loading={loading}
            messages={messages}
            viewerRole={user?.role}
            userId={user?.id}
            editingId={editing?.id ?? null}
            onEditMessage={startEdit}
            onDeleteMessage={removeMessage}
            composer={
               <ChatComposer
                  disabled={loading}
                  sending={sending}
                  onSend={handleSend}
                  placeholder="Scrie un mesaj..."
                  editing={editing}
                  onSaveEdit={saveEdit}
                  onCancelEdit={cancelEdit}
               />
            }
         />
      </div>
   );
}

export default function StaffChatPopup({
   conversationId = null,
   student = null,
   openThread = false,
}) {
   const { user } = useContext(UserContext);
   const staff = isStaffRole(user?.role);
   const workspace = useSyncExternalStore(
      subscribeStaffChatWorkspace,
      getStaffChatWorkspaceState,
      getStaffChatWorkspaceState,
   );
   const unreadTotal = getStaffChatUnreadTotal();
   // Staff list/thread chrome must follow workspace.view only.
   // openThread is a one-shot open hint — if it stays in the class check,
   // going "back" keeps --conversation (padding:0) and sticks content to edges.
   const inConversation = !staff || workspace.view === "thread";

   useEffect(() => {
      if (openThread) {
         setStaffChatView("thread");
      }
   }, [openThread]);

   useEffect(
      () => () => {
         // Do not wipe workspace selection on unmount — only toast context.
         setChatToastContext({ popupOpen: false, conversationId: null });
      },
      [],
   );

   const titleBase = staff ? "Mesaje" : "Mesaje";
   const title =
      staff && unreadTotal > 0 ? `${titleBase} (${unreadTotal})` : titleBase;

   return (
      <div
         className={
            "staffChatPopup" +
            (inConversation ? " staffChatPopup--conversation" : "")
         }
      >
         <div className="staffChatPopup__header">
            <h3 className="staffChatPopup__title">{title}</h3>
         </div>
         {staff ? (
            <StaffChatInboxView
               onConversationChange={() => {}}
               initialConversationId={openThread ? conversationId : null}
               initialStudent={openThread ? student : null}
            />
         ) : (
            <StudentChatView />
         )}
      </div>
   );
}

